import React from 'react';
import PropTypes from 'prop-types';
import RosterWeekView from '../../roster/RosterWeekView/RosterWeekView';
import {
  durationInRosterHourDisplayFormat,
  isDayPartOfWeek,
  isOnSameDate,
  serializeYesplanAPIDate,
  toYesplanDateTimeObject,
  YPDateTimeObjectPropType
} from '../../libs/dateTimeUtilities';
import { dateFormatOptions } from '../../libs/dateFormatting';
import { timeFormatOptions } from '../../libs/timeFormatting';
import RequestHandler from '../../libs/requestHandling';
import ContainerWrapper from '../../styled components/ContainerWrapper';
import { WeekCounterText } from './RosterContainer.style';
import WeekNavigation from '../../components/WeekNavigation/WeekNavigation';
import { withRouter } from '../../libs/widgetUtilities';
import DateNavigationUrlWrapper from '../../components/DateNavigationUrlWrapper';
import RosterNotifications from '../../yesplan_classes/YPRosterNotificationsObject';
import NoPermissionOnResourceError from '../../components/Errors/NoPermissionOnResourceError';

class RosterContainer_ extends React.Component {
  static widgetName = 'roster';

  static getDerivedStateFromProps(props) {
    const { dateObject, week } = props.getDateAndWeekFromUrlOrFromCurrentDate(
      RosterContainer_.widgetName
    );
    return {
      currentDateObject: dateObject,
      currentWeek: week
    };
  }

  constructor(props) {
    super(props);

    const { dateObject, week } = props.getDateAndWeekFromUrlOrFromCurrentDate(
      RosterContainer_.widgetName
    );
    const { humanResource } = this.props;
    props.replaceUrlWithWeekAndDateUrl(RosterContainer_.widgetName);

    this.state = {
      currentDateObject: dateObject,
      currentWeek: week,
      schedules: [],
      bookings: [],
      events: [],
      counters: [],
      allContentFetched: {
        rosters: humanResource === null
      }
    };

    /* Do not attempt to fetch additional data if the resource is null (no permissions) */
    if (humanResource !== null) {
      this.fetchSchedulesAndCounters();
    }

    this.rosterNotificationsVersionstamp =
      this.props.rosternotifications.versionStampQuantity;

    this.getLinkUrlForCurrentWeekAndDay = this.getLinkUrlForCurrentWeekAndDay.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.humanResource !== null &&
      this.props.rosternotifications.versionStampQuantity >
        this.rosterNotificationsVersionstamp
    ) {
      this.rosterNotificationsVersionstamp =
        this.props.rosternotifications.versionStampQuantity;

      const delta = this.props.rosternotifications.deltaRosterNotifications;
      if (
        delta.some((rosterNotification) =>
          rosterNotification.dateObjects.some((dateObj) =>
            isDayPartOfWeek(dateObj, this.state.currentWeek)
          )
        )
      )
        this.fetchSchedulesAndCounters();
    }
  }

  getRosters(startDateObject, endDateObject) {
    return new RequestHandler().get(
      '/rosterschedules',
      {
        yesplanurl: this.props.user.host,
        resourceid: this.props.user.id,
        userid: this.props.user.id,
        from: serializeYesplanAPIDate(startDateObject),
        to: serializeYesplanAPIDate(endDateObject)
      },
      (res, error) => {
        if (error) return error;
        return {
          schedules: res.body.schedules.sort(
            (s1, s2) =>
              toYesplanDateTimeObject(s1.start) - toYesplanDateTimeObject(s2.start)
          ),
          bookings: res.body.bookings,
          events: res.body.events,
          rostersResFrom: startDateObject,
          rostersResTo: endDateObject
        };
      }
    );
  }

  getCounters(startDateObject, endDateObject) {
    const { user } = this.props;
    return new RequestHandler().get(
      '/countersForWeek',
      {
        yesplanurl: user.host,
        resourceid: user.id,
        userid: user.id,
        from: serializeYesplanAPIDate(startDateObject),
        to: serializeYesplanAPIDate(endDateObject)
      },
      (res, error) => {
        if (error) return error;
        return {
          counters: res.body.counters,
          countersResFrom: startDateObject,
          countersResTo: endDateObject
        };
      }
    );
  }

  getLinkUrlForCurrentWeekAndDay(dateObject, reset) {
    const { currentDateObject, currentWeek } = this.state;
    const { getUrlForWeekAndDay } = this.props;
    let urlDateObject = dateObject;
    if (reset) {
      if (isOnSameDate(dateObject, currentDateObject)) urlDateObject = null;
      else urlDateObject = currentDateObject;
    }
    return getUrlForWeekAndDay(RosterContainer_.widgetName, currentWeek, urlDateObject);
  }

  fetchSchedulesAndCounters() {
    const { startDateObject, endDateObject } = this.state.currentWeek;
    Promise.all([
      this.getRosters(startDateObject, endDateObject),
      this.getCounters(startDateObject, endDateObject)
    ]).then((values) => {
      const rostersResult = values[0];
      const countersResult = values[1];
      const { rostersResFrom } = rostersResult;
      const errorStatus = rostersResult.code || countersResult.code;
      if (errorStatus) {
        /* In order to trigger the surrounding ErrorBoundary, we need to do a setState
                 and throw the error in its callback. */
        this.setState(() => {
          throw new Error(`Could not fetch roster schedules: status ${errorStatus}`);
        });
      } else {
        /* Check if we received result for the current week */
        const currentFromDateObject = this.state.currentWeek.startDateObject;
        if (rostersResFrom.isSame(currentFromDateObject)) {
          this.setState({
            ...rostersResult,
            counters: countersResult.counters,
            allContentFetched: {
              roster: true
            }
          });
        }
      }
    });
  }

  renderWeekCounter() {
    const { counters } = this.state;
    const actualCounter = counters.length > 0 ? counters[0].week.actual : null;
    const plannedCounter = counters.length > 0 ? counters[0].week.planned : 0;

    return (
      <div tw="absolute right-0 top-1/2 transform -translate-y-1/2">
        <WeekCounterText hidden={actualCounter}>
          {durationInRosterHourDisplayFormat(plannedCounter)}
        </WeekCounterText>
        {actualCounter && (
          <WeekCounterText withActualHours data-test-type="week-actualHours">
            {durationInRosterHourDisplayFormat(actualCounter)}
          </WeekCounterText>
        )}
      </div>
    );
  }

  renderNoPermission() {
    return <NoPermissionOnResourceError title={i18n('Sorry! No rosters for you… yet')} />;
  }

  render() {
    const {
      rosternotifications,
      humanResource,
      goToUrlForNextWeek,
      goToUrlForPreviousWeek,
      dateFormatSetting,
      timeFormatSetting,
      dayline
    } = this.props;
    const { currentWeek, currentDateObject, schedules, bookings, events, counters } =
      this.state;
    let showCounters = true;
    const allContentFetched = Object.values(this.state.allContentFetched).every(
      (fetched) => fetched
    );
    if (humanResource === null) return this.renderNoPermission();
    if (humanResource.rosterpublisheduntil) {
      const dayBeforePublishedUntilDayObject = humanResource.rosterpublisheduntil
        .clone()
        .add(-1, 'd');

      if (dayBeforePublishedUntilDayObject.isBefore(currentWeek.startDateObject))
        showCounters = false;
    }

    return (
      <ContainerWrapper>
        <div tw="relative">
          <WeekNavigation
            currentWeek={currentWeek}
            goToPreviousWeek={() =>
              goToUrlForPreviousWeek(RosterContainer_.widgetName, currentWeek)
            }
            goToNextWeek={() =>
              goToUrlForNextWeek(RosterContainer_.widgetName, currentWeek)
            }
          />
          {allContentFetched && showCounters && this.renderWeekCounter()}
        </div>
        <div>
          <RosterWeekView
            {...this.props}
            currentRosterDayDateObject={currentDateObject}
            week={currentWeek}
            schedules={schedules}
            bookings={bookings}
            events={events}
            counters={counters}
            dayline={dayline}
            dateFormatSetting={dateFormatSetting}
            timeFormatSetting={timeFormatSetting}
            allContentFetched={allContentFetched}
            notifications={rosternotifications}
            getLinkUrlForCurrentWeekAndDay={this.getLinkUrlForCurrentWeekAndDay}
          />
        </div>
      </ContainerWrapper>
    );
  }
}

RosterContainer_.propTypes = {
  user: PropTypes.shape({
    host: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string
  }).isRequired,
  match: PropTypes.shape({
    path: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    url: PropTypes.string,
    isExact: PropTypes.bool,
    params: PropTypes.shape({
      datePattern: PropTypes.string
    })
  }).isRequired,
  rosternotifications: PropTypes.instanceOf(RosterNotifications).isRequired,
  humanResource: PropTypes.shape({
    rosterpublisheduntil: YPDateTimeObjectPropType
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    hash: PropTypes.string
  }).isRequired,
  linkUrlFor: PropTypes.func.isRequired,
  getWeekAndDayPatternUrl: PropTypes.func.isRequired,
  getDateAndWeekFromUrlOrFromCurrentDate: PropTypes.func.isRequired,
  replaceUrlWithWeekAndDateUrl: PropTypes.func.isRequired,
  goToUrlForNextWeek: PropTypes.func.isRequired,
  goToUrlForPreviousWeek: PropTypes.func.isRequired,
  getUrlForWeekAndDay: PropTypes.func.isRequired,
  dateFormatSetting: PropTypes.oneOf(dateFormatOptions).isRequired,
  timeFormatSetting: PropTypes.oneOf(timeFormatOptions).isRequired,
  dayline: PropTypes.string.isRequired
};

RosterContainer_.defaultProps = {
  humanResource: null
};

const wrapper = (props) => (
  <DateNavigationUrlWrapper {...props}>
    {(propsFromWrapper) => <RosterContainer {...propsFromWrapper} />}
  </DateNavigationUrlWrapper>
);
const RosterContainer = withRouter(RosterContainer_);

export default wrapper;
